import type { ReactNode } from "react";
import type { GridColumn } from "@fortex/map";
import type { AppLang } from "../i18n";

export type TableRow = Record<string, unknown>;

export const tableIdColumn = "Id";

export const tablePageSizes = [25, 50, 100];

export const tableColumnWidth = { width: "200px", minWidth: 100, maxWidth: 1000 };

const dateTimePattern = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;

export function hasHistory(columns: string[]): boolean {
  return columns.includes("ValidFrom") && columns.includes("ValidTo");
}

export function dateTimeText(value: string, lang: AppLang): string | null {
  const match = dateTimePattern.exec(value);

  if (match === null) return null;

  const [, year, month, day, hour, minute, second, zone] = match;
  const offset = zone === undefined ? "" : ` ${zone === "Z" ? "+00:00" : zone}`;

  return lang === "en" ? `${day}/${month}/${year} ${hour}:${minute}:${second}${offset}` : `${year}. ${month}. ${day}. ${Number(hour)}:${minute}:${second}${offset}`;
}

export function cellText(value: unknown, lang: AppLang): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "True" : "False";
  if (typeof value === "number") return lang === "en" ? String(value) : String(value).replace(".", ",");
  if (typeof value === "string") return dateTimeText(value, lang) ?? value;

  return JSON.stringify(value);
}

export function rowKeyColumns(columns: string[]): string[] {
  const id = columns.find(a => a.toLowerCase() === tableIdColumn.toLowerCase());

  return id === undefined ? [] : [id, ...["ValidFrom", "ValidTo"].filter(a => columns.includes(a))];
}

export function rowKeysOf(columns: string[], rows: TableRow[]): string[] {
  const parts = rowKeyColumns(columns);
  const seen = new Map<string, number>();

  return rows.map(row => {
    const base = parts.length === 0 ? JSON.stringify(row) : parts.map(a => String(row[a] ?? "")).join("|");
    const count = seen.get(base) ?? 0;

    seen.set(base, count + 1);

    return count === 0 ? base : `${base}#${count}`;
  });
}

export function tableGridColumns(columns: string[], renderCell: (row: TableRow, column: string) => ReactNode): GridColumn<TableRow>[] {
  return columns.map(name => ({
    key: name,
    header: name,
    field: name,
    sortable: true,
    ...tableColumnWidth,
    filter: { kind: "text", operators: ["Contains"] },
    render: row => renderCell(row, name)
  }));
}
